import { useLayoutEffect, useRef } from 'react'
import { marked } from 'marked'
import Prism from 'prismjs'
import 'prismjs/themes/prism-tomorrow.css'
import 'prismjs/components/prism-markup'
import 'prismjs/components/prism-markup-templating'
import 'prismjs/components/prism-css'
import 'prismjs/components/prism-javascript'
import 'prismjs/components/prism-typescript'
import 'prismjs/components/prism-jsx'
import 'prismjs/components/prism-tsx'
import 'prismjs/components/prism-python'
import 'prismjs/components/prism-java'
import 'prismjs/components/prism-c'
import 'prismjs/components/prism-cpp'
import 'prismjs/components/prism-csharp'
import 'prismjs/components/prism-php'
import 'prismjs/components/prism-ruby'
import 'prismjs/components/prism-go'
import 'prismjs/components/prism-rust'
import 'prismjs/components/prism-sql'
import 'prismjs/components/prism-bash'
import 'prismjs/components/prism-json'
import 'prismjs/components/prism-yaml'
import 'prismjs/components/prism-markdown'
import { typesetMathInElement } from '../../lib/mathjaxTypeset'

marked.setOptions({ breaks: true, gfm: true })

function fixMathDelimiters(text: string) {
  let t = text
  t = t.replace(/\[([^\]]*\\[^\]]*)\]/g, (_, expr) => `$$${expr}$$`)
  t = t.replace(/\(([^()]*\\[^()]*)\)/g, (_, expr) => `\\(${expr}\\)`)
  return t
}

function highlightCodeBlocks(root: HTMLElement) {
  root.querySelectorAll('pre code').forEach((block) => {
    const codeElement = block as HTMLElement
    const classList = Array.from(codeElement.classList)
    const langClass = classList.find(cls => cls.startsWith('language-'))
    const language = langClass ? langClass.replace('language-', '') : 'plaintext'

    if (language && language !== 'plaintext' && Prism.languages[language]) {
      try {
        Prism.highlightElement(codeElement)
      } catch (e) {
        console.warn('Failed to highlight code block with language:', language, e)
        try {
          Prism.highlightElement(codeElement, Prism.languages.plaintext || Prism.languages.markup, 'plaintext')
        } catch (fallbackError) {
          console.warn('Failed to highlight with plaintext fallback:', fallbackError)
        }
      }
    } else {
      try {
        Prism.highlightElement(codeElement, Prism.languages[language] || Prism.languages.plaintext || Prism.languages.markup, language || 'plaintext')
      } catch (e) {
        console.warn('Failed to highlight code block:', e)
      }
    }
  })
}

function tableToTSV(table: HTMLTableElement): string {
  const rows = Array.from(table.querySelectorAll('tr'))
  return rows
    .map((row) =>
      Array.from(row.querySelectorAll('th,td'))
        .map((cell) => (cell.textContent ?? '').replace(/\s+/g, ' ').trim())
        .join('\t'),
    )
    .join('\n')
    .trim()
}

interface V2MarkdownProps {
  content: string
  /** When true, skip Prism so each token chunk can repaint without heavy work. */
  isStreaming?: boolean
  className?: string
  compact?: boolean
}

/**
 * Chat v2 assistant markdown: same output as shared MarkdownMessage, but defers
 * syntax highlighting until streaming completes so markdown layout updates in realtime.
 */
export function V2Markdown({ content, isStreaming = false, className = '', compact = false }: V2MarkdownProps) {
  const contentRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const el = contentRef.current
    if (!el) return

    let streamContent = content
    if (isStreaming) {
      // Close any unclosed code fence so marked renders a proper block
      const fenceCount = (streamContent.match(/^```/gm) ?? []).length
      if (fenceCount % 2 !== 0) streamContent += '\n```'
      streamContent += '\n\n'
    }
    const fixed = fixMathDelimiters(streamContent)
    const parsed = marked.parse(fixed)
    const html = typeof parsed === 'string' ? parsed : String(parsed)
    el.innerHTML = html

    highlightCodeBlocks(el)

    // Always wrap tables so horizontal scroll happens inside the message bubble,
    // including `compact` mode (used in chat v2).
    el.querySelectorAll('table').forEach((table) => {
      const element = table as HTMLTableElement

      let wrap = element.closest('.v2-table-wrap') as HTMLDivElement | null
      if (!wrap) {
        wrap = document.createElement('div')
        wrap.className = 'v2-table-wrap'
        element.parentElement?.insertBefore(wrap, element)
        wrap.appendChild(element)
      }

      // Ensure the horizontal scroll happens inside an inner container so the
      // "Copy" button doesn't move when the table is scrolled.
      let scroll = wrap.querySelector(':scope > .v2-table-scroll') as HTMLDivElement | null
      if (!scroll) {
        scroll = document.createElement('div')
        scroll.className = 'v2-table-scroll'
        // If the table is a direct child of the wrapper, move it inside the scroll container.
        if (element.parentElement === wrap) {
          wrap.appendChild(scroll)
          scroll.appendChild(element)
        } else {
          // wrapper exists but table isn't a direct child (unlikely) — still ensure scroll exists
          wrap.appendChild(scroll)
          scroll.appendChild(element)
        }
      } else {
        // If the table isn't inside the scroll container yet, move it.
        if (element.parentElement !== scroll) {
          scroll.appendChild(element)
        }
      }

      element.style.borderCollapse = 'collapse'
      element.style.width = 'max-content'
      element.style.minWidth = '100%'
    })

    // Add "Copy table" affordance (matches previous chat UX).
    el.querySelectorAll('.v2-table-wrap').forEach((wrap) => {
      const container = wrap as HTMLDivElement
      const table = container.querySelector('table') as HTMLTableElement | null
      if (!table) return

      let btn = container.querySelector(':scope > button.v2-table-copy') as HTMLButtonElement | null
      if (!btn) {
        btn = document.createElement('button')
        btn.type = 'button'
        btn.className = 'v2-table-copy'
        btn.textContent = 'Copy'
        btn.setAttribute('aria-label', 'Copy table to clipboard')
        container.appendChild(btn)
      }

      btn.onclick = async (e) => {
        e.preventDefault()
        e.stopPropagation()
        try {
          const text = tableToTSV(table)
          await navigator.clipboard?.writeText(text)
          btn!.textContent = 'Copied'
          window.setTimeout(() => {
            if (btn) btn.textContent = 'Copy'
          }, 1200)
        } catch {
          btn!.textContent = 'Failed'
          window.setTimeout(() => {
            if (btn) btn.textContent = 'Copy'
          }, 1200)
        }
      }
    })

    if (!compact) {
      el.querySelectorAll('pre').forEach((pre) => {
        const element = pre as HTMLElement
        element.style.borderRadius = '8px'
        element.style.padding = '16px'
        element.style.overflowX = 'auto'
        element.style.margin = '12px 0'
      })

      el.querySelectorAll('code').forEach((code) => {
        if (!code.parentElement?.tagName.match(/pre/i)) {
          const element = code as HTMLElement
          element.style.padding = '2px 6px'
          element.style.borderRadius = '4px'
          element.style.fontSize = '0.75em'
        }
      })

      // table scrolling handled by `.v2-table-wrap` styles
    }

    el.querySelectorAll('th, td').forEach((cell) => {
      const element = cell as HTMLElement
      element.style.border = '1px solid'
      element.style.borderColor = 'rgba(0,0,0,0.1)'
      element.style.padding = '8px 12px'
      element.style.whiteSpace = 'nowrap'
    })

    el.querySelectorAll('th').forEach((th) => {
      const element = th as HTMLElement
      element.style.backgroundColor = 'rgba(0,0,0,0.05)'
      element.style.fontWeight = '600'
    })

    if (!compact) {
      el.querySelectorAll('ul, ol').forEach((list) => {
        const element = list as HTMLElement
        element.style.marginTop = '8px'
        element.style.marginBottom = '8px'
        element.style.paddingLeft = '24px'
      })

      el.querySelectorAll('li').forEach((li) => {
        const element = li as HTMLElement
        element.style.marginTop = '4px'
        element.style.marginBottom = '4px'
      })
    }

    typesetMathInElement(el)
  }, [content, isStreaming, compact])


  return (
    <div
      ref={contentRef}
      className={`text-sm leading-relaxed prose prose-sm max-w-none ${className} prose-gray dark:prose-invert prose-p:text-gray-900 dark:prose-p:text-white prose-headings:text-gray-900 dark:prose-headings:text-white prose-a:text-indigo-600 dark:prose-a:text-indigo-400 prose-code:bg-gray-100 dark:prose-code:bg-gray-700 prose-pre:bg-gray-50 dark:prose-pre:bg-gray-800`}
      style={{ wordBreak: 'break-word' }}
    />
  )
}
